import { useEffect, useMemo, useRef, useState } from 'react';
import {
  X,
  Plus,
  Trash2,
  Paperclip,
  Loader2,
  Check,
  Repeat,
  Users,
  Pencil,
  ChevronDown,
  ChevronUp,
  Search,
  LayoutTemplate,
  Settings2,
} from 'lucide-react';
import api from '../../api/client';
import { Modal } from '../common/Modal';
import { useProject } from '../../hooks/useProject';
import type { User, UserGroup, TaskTemplate, Task } from '../../types';

interface TaskModalProps {
  onClose: () => void;
  onCreated: () => void;
  users: User[];
  groups?: UserGroup[];
  editTask?: (Partial<Task> & { id: number }) | null;
}

interface AssignableItem {
  id: number;
  type: 'user' | 'group';
  name: string;
  subtitle?: string;
  color?: string;
}

export default function TaskModal({ onClose, onCreated, users, groups = [], editTask }: TaskModalProps) {
  const { projects, selectedProject, refreshProjects } = useProject();

  const [title, setTitle] = useState(editTask?.title || '');
  const [description, setDescription] = useState(editTask?.description || '');
  const [projectId, setProjectId] = useState<number | ''>(editTask?.project_id || selectedProject?.id || '');
  const [assignedTo, setAssignedTo] = useState<number[]>(editTask?.assigned_users?.map((u) => u.id) || []);
  const [assignedGroupIds, setAssignedGroupIds] = useState<number[]>(editTask?.assigned_groups?.map((g) => g.id) || []);
  const [isRecurring, setIsRecurring] = useState<boolean>(editTask?.is_recurring || false);
  const [recurringType, setRecurringType] = useState<string>(editTask?.recurring_pattern?.type || 'weekly');
  const [recurringInterval, setRecurringInterval] = useState<number>(editTask?.recurring_pattern?.interval || 1);
  const [recurringEndDate, setRecurringEndDate] = useState<string>(editTask?.recurring_ends_at?.slice(0, 10) || '');
  const [recurringOccurrences, setRecurringOccurrences] = useState<number | ''>(editTask?.recurring_occurrences || '');
  const [priority, setPriority] = useState<string>(editTask?.priority || 'orta');
  const [dueDate, setDueDate] = useState(editTask?.due_date?.slice(0, 10) || '');
  const [subTasks, setSubTasks] = useState<{ id?: number; title: string; is_completed?: boolean }[]>([]);
  const [newSubTask, setNewSubTask] = useState('');
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<number | ''>('');
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [managingTemplates, setManagingTemplates] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<number | null>(null);
  const [editTemplateName, setEditTemplateName] = useState('');
  const [editTemplateTitle, setEditTemplateTitle] = useState('');
  const [editTemplateDescription, setEditTemplateDescription] = useState('');
  const [editTemplatePriority, setEditTemplatePriority] = useState('orta');
  const [templateActionLoading, setTemplateActionLoading] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionPos, setMentionPos] = useState({ top: 0, left: 0 });
  const [assigneeQuery, setAssigneeQuery] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const descRef = useRef<HTMLTextAreaElement>(null);
  const submitLockRef = useRef(false);
  const idempotencyKeyRef = useRef<string>(crypto.randomUUID());

  useEffect(() => {
    if (projects.length === 0) {
      refreshProjects().catch(() => {});
    }
  }, [projects.length, refreshProjects]);

  useEffect(() => {
    if (editTask) {
      setTitle(editTask.title ?? '');
      setDescription(editTask.description ?? '');
      setProjectId(editTask.project_id || selectedProject?.id || projects[0]?.id || '');
      setAssignedTo(editTask.assigned_users?.map((u) => u.id) || []);
      setAssignedGroupIds(editTask.assigned_groups?.map((g) => g.id) || []);
      setIsRecurring(editTask.is_recurring || false);
      setRecurringType(editTask.recurring_pattern?.type || 'weekly');
      setRecurringInterval(editTask.recurring_pattern?.interval || 1);
      setRecurringEndDate(editTask.recurring_ends_at?.slice(0, 10) || '');
      setRecurringOccurrences(editTask.recurring_occurrences ?? '');
      setPriority(editTask.priority || 'orta');
      setDueDate(editTask.due_date?.slice(0, 10) || '');
      if (editTask.is_recurring) {
        setAdvancedOpen(true);
      }
    } else if (projectId === '' && projects.length > 0) {
      // Sadece ilk yukleniste veya kullanici tarafindan bosaltilmadiysa varsayilan proje sec.
      setProjectId(selectedProject?.id || projects[0].id);
    }
    // projectId dependency olarak eklenmez; kullanicinin bosaltmasina izin ver.
  }, [editTask, selectedProject, projects]);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await api.get('/api/v1/task-templates');
        setTemplates(res.data?.data || []);
      } catch {
        // ignore
      }
    };
    fetchTemplates();
  }, []);

  useEffect(() => {
    const q = assigneeQuery.trim();
    if (!q || q.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setIsSearchingUsers(true);
      try {
        const res = await api.get(`/api/v1/users/search?search=${encodeURIComponent(q)}&per_page=50&exclude_self=false`);
        setSearchResults(res.data?.data || []);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearchingUsers(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [assigneeQuery]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const safeUsers = useMemo(() => Array.isArray(users) ? users : [], [users]);
  const safeGroups = useMemo(() => Array.isArray(groups) ? groups : [], [groups]);

  const allUsers = useMemo(() => {
    const map = new Map<number, User>();
    safeUsers.forEach((u) => {
      if (u && u.id != null) map.set(u.id, u);
    });
    searchResults.forEach((u) => {
      if (u && u.id != null) map.set(u.id, u);
    });
    return Array.from(map.values());
  }, [safeUsers, searchResults]);

  const assignableItems: AssignableItem[] = useMemo(() => {
    const userItems: AssignableItem[] = allUsers
      .filter((u) => u && u.id != null)
      .map((u) => ({
        id: u.id,
        type: 'user',
        name: `${u.name ?? ''} ${u.surname ?? ''}`.trim() || (u.email ?? 'Kullanıcı'),
        subtitle: u.email,
      }));
    const groupItems: AssignableItem[] = safeGroups
      .filter((g) => g && g.id != null)
      .map((g) => ({
        id: g.id,
        type: 'group',
        name: g.name || 'Grup',
        color: g.color || undefined,
      }));
    return [...userItems, ...groupItems];
  }, [allUsers, safeGroups]);

  const filteredItems = useMemo(() => {
    const q = assigneeQuery.trim().toLowerCase();
    if (!q) return assignableItems;
    return assignableItems.filter(
      (i) =>
        (i.name || '').toLowerCase().includes(q) ||
        (i.subtitle && i.subtitle.toLowerCase().includes(q))
    );
  }, [assignableItems, assigneeQuery]);

  const selectedItems: AssignableItem[] = useMemo(() => {
    const selected: AssignableItem[] = [];
    assignedTo.forEach((id) => {
      const u = safeUsers.find((x) => x && x.id === id);
      if (u) selected.push({ id: u.id, type: 'user', name: `${u.name ?? ''} ${u.surname ?? ''}`.trim() || (u.email ?? 'Kullanıcı'), subtitle: u.email });
    });
    assignedGroupIds.forEach((id) => {
      const g = safeGroups.find((x) => x && x.id === id);
      if (g) selected.push({ id: g.id, type: 'group', name: g.name || 'Grup', color: g.color || undefined });
    });
    return selected;
  }, [assignedTo, assignedGroupIds, safeUsers, safeGroups]);

  const toggleItem = (item: AssignableItem) => {
    if (item.type === 'user') {
      setAssignedTo((prev) =>
        prev.includes(item.id) ? prev.filter((id) => id !== item.id) : [...prev, item.id]
      );
    } else {
      setAssignedGroupIds((prev) =>
        prev.includes(item.id) ? prev.filter((id) => id !== item.id) : [...prev, item.id]
      );
    }
  };

  const removeSelected = (item: AssignableItem) => {
    if (item.type === 'user') {
      setAssignedTo((prev) => prev.filter((id) => id !== item.id));
    } else {
      setAssignedGroupIds((prev) => prev.filter((id) => id !== item.id));
    }
  };

  const handleDescChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setDescription(val);

    const cursor = e.target.selectionStart;
    const beforeCursor = val.slice(0, cursor);
    const lastAt = beforeCursor.lastIndexOf('@');
    if (lastAt !== -1 && lastAt === beforeCursor.length - 1) {
      setMentionOpen(true);
      setMentionQuery('');
      const rect = e.target.getBoundingClientRect();
      setMentionPos({ top: rect.bottom, left: rect.left });
    } else if (lastAt !== -1 && !beforeCursor.slice(lastAt).includes(' ')) {
      setMentionQuery(beforeCursor.slice(lastAt + 1));
      setMentionOpen(true);
    } else {
      setMentionOpen(false);
    }
  };

  const insertMention = (u: User) => {
    const el = descRef.current;
    if (!el) return;
    const cursor = el.selectionStart;
    const val = description;
    const beforeCursor = val.slice(0, cursor);
    const lastAt = beforeCursor.lastIndexOf('@');
    const before = val.slice(0, lastAt);
    const after = val.slice(cursor);
    const newVal = `${before}@${u.name}_${u.surname} ${after}`;
    setDescription(newVal);
    setMentionOpen(false);
    setTimeout(() => {
      el.focus();
      const pos = before.length + u.name.length + u.surname.length + 2;
      el.setSelectionRange(pos, pos);
    }, 0);
  };

  const filteredUsers = safeUsers.filter((u) =>
    `${u.name ?? ''} ${u.surname ?? ''}`.toLowerCase().includes(mentionQuery.toLowerCase())
  );

  const handleAddSubTask = () => {
    if (!newSubTask.trim()) return;
    setSubTasks((prev) => [...prev, { title: newSubTask.trim() }]);
    setNewSubTask('');
  };

  const removeSubTask = (idx: number) => {
    setSubTasks((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleTemplateChange = async (templateId: number) => {
    setSelectedTemplate(templateId);
    if (!templateId) return;
    try {
      const res = await api.get(`/api/v1/task-templates/${templateId}`);
      const data = res.data?.data || res.data;
      if (data) {
        if (data.title_template) setTitle(data.title_template);
        if (data.description_template) setDescription(data.description_template);
        if (data.default_priority) setPriority(data.default_priority);
      }
    } catch {
      // ignore
    }
  };

  const startEditTemplate = (t: TaskTemplate) => {
    setEditingTemplateId(t.id);
    setEditTemplateName((t as unknown as Record<string, string>).name || t.name || '');
    setEditTemplateTitle((t as unknown as Record<string, string>).title_template || t.name || '');
    setEditTemplateDescription((t as unknown as Record<string, string>).description_template || '');
    setEditTemplatePriority((t as unknown as Record<string, string>).default_priority || 'orta');
  };

  const cancelEditTemplate = () => {
    setEditingTemplateId(null);
    setEditTemplateName('');
    setEditTemplateTitle('');
    setEditTemplateDescription('');
    setEditTemplatePriority('orta');
  };

  const saveTemplate = async () => {
    if (!editingTemplateId || templateActionLoading) return;
    setTemplateActionLoading(true);
    try {
      await api.put(`/api/v1/task-templates/${editingTemplateId}`, {
        name: editTemplateName,
        title_template: editTemplateTitle,
        description_template: editTemplateDescription,
        default_priority: editTemplatePriority,
      });
      const res = await api.get('/api/v1/task-templates');
      setTemplates(res.data?.data || []);
      cancelEditTemplate();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Şablon güncellenirken bir hata oluştu.';
      setError(msg);
    } finally {
      setTemplateActionLoading(false);
    }
  };

  const deleteTemplate = async (templateId: number) => {
    if (templateActionLoading) return;
    setTemplateActionLoading(true);
    try {
      await api.delete(`/api/v1/task-templates/${templateId}`);
      const res = await api.get('/api/v1/task-templates');
      setTemplates(res.data?.data || []);
      if (selectedTemplate === templateId) setSelectedTemplate('');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Şablon silinirken bir hata oluştu.';
      setError(msg);
    } finally {
      setTemplateActionLoading(false);
    }
  };

  const clearFieldError = (field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      errors.title = 'Görev başlığı zorunludur.';
    } else if (trimmedTitle.length > 255) {
      errors.title = 'Görev başlığı en fazla 255 karakter olabilir.';
    }
    if (!projectId) {
      errors.project_id = 'Proje seçimi zorunludur.';
    }
    if (dueDate && !/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) {
      errors.due_date = 'Son tarih geçerli bir tarih olmalıdır.';
    }
    if (isRecurring) {
      if (!['daily', 'weekly', 'monthly'].includes(recurringType)) {
        errors.recurring_type = 'Geçersiz tekrar tipi.';
      }
      if (!recurringInterval || recurringInterval < 1 || recurringInterval > 12) {
        errors.recurring_interval = 'Tekrar aralığı 1-12 arasında olmalıdır.';
      }
      if (recurringEndDate && dueDate && recurringEndDate < dueDate) {
        errors.recurring_ends_at = 'Tekrar bitiş tarihi, son tarihten önce olamaz.';
      }
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitLockRef.current || isLoading) return;
    if (!validate()) return;

    submitLockRef.current = true;
    setError(null);
    setIsLoading(true);

    let createdTaskId: number | undefined;

    try {
      const payload = {
        title: title.trim(),
        description: description || null,
        project_id: projectId || null,
        assigned_user_ids: assignedTo,
        assigned_group_ids: assignedGroupIds,
        priority,
        due_date: dueDate || null,
        is_recurring: isRecurring,
        recurring_pattern: isRecurring ? { type: recurringType, interval: Number(recurringInterval) || 1 } : null,
        recurring_ends_at: isRecurring ? (recurringEndDate || null) : null,
        recurring_occurrences: isRecurring ? (recurringOccurrences ? Number(recurringOccurrences) : null) : null,
        sub_tasks: subTasks.filter((s) => !s.id).map((s) => ({ title: s.title.trim() })),
      };

      if (editTask?.id) {
        const res = await api.put(`/api/v1/tasks/${editTask.id}`, payload, {
          headers: { 'Idempotency-Key': idempotencyKeyRef.current },
        });
        createdTaskId = res.data?.data?.id || editTask.id;
      } else {
        const res = await api.post('/api/v1/tasks', payload, {
          headers: { 'Idempotency-Key': idempotencyKeyRef.current },
        });
        createdTaskId = res.data?.data?.id;
      }

      if (!createdTaskId) {
        throw new Error('Görev kimliği alınamadı.');
      }

      if (files.length > 0) {
        try {
          for (const file of files) {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('task_id', String(createdTaskId));
            formData.append('project_id', String(projectId));
            await api.post('/api/v1/files', formData);
          }
        } catch (fileErr: unknown) {
          const axiosErr = fileErr as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } };
          const fileMsg = axiosErr?.response?.data?.message || 'Dosyalar yüklenirken bir hata oluştu.';
          if (axiosErr?.response?.data?.errors) {
            const backendErrors: Record<string, string> = {};
            Object.entries(axiosErr.response.data.errors).forEach(([key, values]) => {
              backendErrors[key] = values[0];
            });
            setFieldErrors((prev) => ({ ...prev, ...backendErrors }));
          }
          setError(`Görev oluşturuldu ancak dosyalar yüklenemedi: ${fileMsg} Görev detayından dosyaları tekrar ekleyebilirsiniz.`);
          setIsLoading(false);
          submitLockRef.current = false;
          return;
        }
      }

      onCreated();
      onClose();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } };
      const msg = axiosErr?.response?.data?.message || 'Bir hata oluştu. Lütfen tekrar deneyin.';
      setError(msg);

      if (axiosErr?.response?.data?.errors) {
        const backendErrors: Record<string, string> = {};
        Object.entries(axiosErr.response.data.errors).forEach(([key, values]) => {
          backendErrors[key] = values[0];
        });
        setFieldErrors((prev) => ({ ...prev, ...backendErrors }));
      }

      submitLockRef.current = false;
    } finally {
      setIsLoading(false);
    }
  };

  const renderTemplateSection = () => {
    if (editTask) return null;
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50">
        <button
          type="button"
          onClick={() => setTemplatesOpen((p) => !p)}
          data-testid="toggle-templates-section"
          className="flex w-full items-center justify-between px-3 py-2.5 text-left sm:px-4 sm:py-3"
        >
          <span className="flex items-center gap-2 text-xs font-medium text-slate-700 sm:text-sm">
            <LayoutTemplate className="h-4 w-4 text-slate-500" />
            Şablon ile başla / Şablonları yönet
          </span>
          {templatesOpen ? (
            <ChevronUp className="h-4 w-4 text-slate-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-500" />
          )}
        </button>

        {templatesOpen && (
          <div className="space-y-3 border-t border-slate-200 px-3 pb-3 pt-2 sm:px-4 sm:pb-4 sm:pt-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-slate-500">Şablon Kullan</label>
              <button
                type="button"
                onClick={() => setManagingTemplates((p) => !p)}
                data-testid="toggle-template-manager"
                className="text-xs font-medium text-blue-600 hover:text-blue-700"
              >
                {managingTemplates ? 'Yönetimi Kapat' : 'Şablonları Yönet'}
              </button>
            </div>
            <select
              disabled={isLoading}
              value={selectedTemplate}
              onChange={(e) => handleTemplateChange(Number(e.target.value))}
              className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-blue-500 disabled:bg-slate-100 sm:px-3 sm:py-2 sm:text-sm"
            >
              <option value="">Şablon seç...</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>

            {managingTemplates && (
              <div className="rounded-lg border border-slate-200 bg-white p-2 space-y-2 sm:p-3">
                {templates.length === 0 && <p className="text-xs text-slate-500">Henüz şablon yok.</p>}
                {templates.map((t) => {
                  const isEditing = editingTemplateId === t.id;
                  const titleTemplate = (t as unknown as Record<string, string>).title_template || t.name;
                  return (
                    <div key={t.id} data-testid={`template-card-${t.id}`} className="rounded-md border border-slate-200 p-2">
                      {isEditing ? (
                        <div className="space-y-2">
                          <input
                            value={editTemplateName}
                            onChange={(e) => setEditTemplateName(e.target.value)}
                            placeholder="Şablon adı"
                            data-testid={`template-name-input-${t.id}`}
                            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
                          />
                          <input
                            value={editTemplateTitle}
                            onChange={(e) => setEditTemplateTitle(e.target.value)}
                            placeholder="Görev başlık şablonu"
                            data-testid={`template-title-input-${t.id}`}
                            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
                          />
                          <textarea
                            value={editTemplateDescription}
                            onChange={(e) => setEditTemplateDescription(e.target.value)}
                            placeholder="Açıklama şablonu"
                            data-testid={`template-description-input-${t.id}`}
                            rows={2}
                            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
                          />
                          <select
                            value={editTemplatePriority}
                            onChange={(e) => setEditTemplatePriority(e.target.value)}
                            data-testid={`template-priority-input-${t.id}`}
                            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
                          >
                            <option value="dusuk">Düşük</option>
                            <option value="orta">Orta</option>
                            <option value="yuksek">Yüksek</option>
                            <option value="acil">Acil</option>
                          </select>
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={cancelEditTemplate}
                              data-testid={`template-cancel-${t.id}`}
                              className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
                            >
                              İptal
                            </button>
                            <button
                              type="button"
                              onClick={saveTemplate}
                              disabled={templateActionLoading || !editTemplateName.trim() || !editTemplateTitle.trim()}
                              data-testid="template-save"
                              className="rounded bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                            >
                              Kaydet
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-slate-800">{t.name}</p>
                            <p className="truncate text-xs text-slate-500">{titleTemplate}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => startEditTemplate(t)}
                              data-testid={`template-edit-${t.id}`}
                              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-600"
                              title="Düzenle"
                              aria-label="Düzenle"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteTemplate(t.id)}
                              data-testid={`template-delete-${t.id}`}
                              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded p-2 text-red-400 hover:bg-red-50 hover:text-red-600"
                              title="Sil"
                              aria-label="Sil"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderAssigneeSection = () => (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-500">
        <Users className="mb-0.5 inline h-3.5 w-3.5" /> Atananlar
      </label>
      <div className="rounded-lg border border-slate-300 bg-white p-2">
        {selectedItems.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {selectedItems.map((item) => (
              <span
                key={`${item.type}-${item.id}`}
                className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium"
                style={{
                  backgroundColor: item.type === 'group' && item.color ? `${item.color}20` : '#dbeafe',
                  color: item.type === 'group' && item.color ? item.color : '#1d4ed8',
                }}
              >
                {item.type === 'group' && (
                  <Users className="h-3 w-3" />
                )}
                {item.name}
                <button
                  type="button"
                  onClick={() => removeSelected(item)}
                  className="flex min-h-[24px] min-w-[24px] items-center justify-center rounded-full hover:bg-white/40"
                  aria-label="Kaldır"
                  title="Kaldır"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="relative mb-2">
          {isSearchingUsers ? (
            <Loader2 className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-500" />
          ) : (
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          )}
          <input
            type="text"
            value={assigneeQuery}
            onChange={(e) => setAssigneeQuery(e.target.value)}
            placeholder="Kullanıcı veya grup ara..."
            className="w-full rounded-md border border-slate-200 bg-slate-50 py-1.5 pl-8 pr-3 text-xs outline-none focus:border-blue-500 sm:py-2 sm:text-sm"
          />
        </div>
        <div className="max-h-32 overflow-y-auto overscroll-contain rounded-md border border-slate-100 p-1 sm:max-h-40 md:max-h-56">
          {filteredItems.map((item) => {
            const selected =
              (item.type === 'user' && assignedTo.includes(item.id)) ||
              (item.type === 'group' && assignedGroupIds.includes(item.id));
            return (
              <button
                key={`${item.type}-${item.id}`}
                type="button"
                disabled={isLoading}
                data-testid={item.type === 'user' ? `assign-user-${item.id}` : `assign-group-${item.id}`}
                onClick={() => toggleItem(item)}
                className={`flex min-h-[44px] w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-xs transition disabled:opacity-50 sm:px-2 sm:py-1.5 sm:text-sm ${
                  selected ? 'bg-blue-50 text-blue-700' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                <div
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border sm:h-5 sm:w-5 ${
                    selected ? 'border-blue-500 bg-blue-500 text-white' : 'border-slate-300 bg-white'
                  }`}
                >
                  {selected && <Check className="h-3.5 w-3.5" />}
                </div>
                <div className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5 truncate">
                    {item.name}
                    {item.type === 'group' && (
                      <span className="shrink-0 rounded bg-slate-100 px-1 py-0 text-[10px] font-medium text-slate-600">
                        Grup
                      </span>
                    )}
                  </span>
                  {item.subtitle && <span className="block truncate text-[10px] text-slate-500 sm:text-xs">{item.subtitle}</span>}
                </div>
              </button>
            );
          })}
          {filteredItems.length === 0 && (
            <p className="px-2 py-2 text-xs text-slate-500">Sonuç bulunamadı.</p>
          )}
        </div>
      </div>
    </div>
  );

  const renderAdvancedSection = () => (
    <div className="rounded-lg border border-slate-200 bg-slate-50">
      <button
        type="button"
        onClick={() => setAdvancedOpen((p) => !p)}
        data-testid="toggle-advanced-section"
        className="flex w-full items-center justify-between px-3 py-2.5 text-left sm:px-4 sm:py-3"
      >
        <span className="flex items-center gap-2 text-xs font-medium text-slate-700 sm:text-sm">
          <Settings2 className="h-4 w-4 text-slate-500" />
          Gelişmiş Ayarlar
          {(isRecurring || subTasks.length > 0 || files.length > 0) && (
            <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
              {[
                isRecurring ? 'Tekrar' : '',
                subTasks.length > 0 ? `${subTasks.length} Alt Görev` : '',
                files.length > 0 ? `${files.length} Dosya` : '',
              ]
                .filter(Boolean)
                .join(', ')}
            </span>
          )}
        </span>
        {advancedOpen ? (
          <ChevronUp className="h-4 w-4 text-slate-500" />
        ) : (
          <ChevronDown className="h-4 w-4 text-slate-500" />
        )}
      </button>

      {advancedOpen && (
        <div className="space-y-4 border-t border-slate-200 px-3 pb-3 pt-2 sm:px-4 sm:pb-4 sm:pt-3">
          {/* Tekrarlayan Görev */}
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <label className="mb-3 flex cursor-pointer items-center gap-2 text-xs font-medium text-slate-700 sm:text-sm">
              <input
                type="checkbox"
                checked={isRecurring}
                disabled={isLoading}
                onChange={(e) => {
                  setIsRecurring(e.target.checked);
                  clearFieldError('recurring_type');
                  clearFieldError('recurring_interval');
                  clearFieldError('recurring_ends_at');
                }}
                data-testid="task-recurring-toggle"
                className="h-4 w-4 rounded border-slate-300 text-blue-600 disabled:opacity-50"
              />
              <Repeat className="h-4 w-4 text-slate-500" />
              Tekrarlayan Görev
            </label>
            {isRecurring && (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3 lg:grid-cols-4">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-500">Sıklık</label>
                  <select
                    disabled={isLoading}
                    value={recurringType}
                    onChange={(e) => {
                      setRecurringType(e.target.value);
                      clearFieldError('recurring_type');
                    }}
                    data-testid="task-recurring-type"
                    className={`w-full rounded-lg border bg-white px-2.5 py-1.5 text-xs outline-none focus:border-blue-500 disabled:bg-slate-100 sm:px-3 sm:py-2 sm:text-sm ${
                      fieldErrors.recurring_type ? 'border-red-300' : 'border-slate-300'
                    }`}
                  >
                    <option value="daily">Günlük</option>
                    <option value="weekly">Haftalık</option>
                    <option value="monthly">Aylık</option>
                  </select>
                  {fieldErrors.recurring_type && <p data-testid="field-error-recurring-type" className="mt-1 text-xs text-red-600">{fieldErrors.recurring_type}</p>}
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-500">
                    Her X {recurringType === 'daily' ? 'gün' : recurringType === 'weekly' ? 'hafta' : 'ay'}
                  </label>
                  <input
                    type="number"
                    min={1}
                    disabled={isLoading}
                    value={recurringInterval}
                    onChange={(e) => {
                      setRecurringInterval(Number(e.target.value));
                      clearFieldError('recurring_interval');
                    }}
                    data-testid="task-recurring-interval"
                    className={`w-full rounded-lg border bg-white px-2.5 py-1.5 text-xs outline-none focus:border-blue-500 disabled:bg-slate-100 sm:px-3 sm:py-2 sm:text-sm ${
                      fieldErrors.recurring_interval ? 'border-red-300' : 'border-slate-300'
                    }`}
                  />
                  {fieldErrors.recurring_interval && <p data-testid="field-error-recurring-interval" className="mt-1 text-xs text-red-600">{fieldErrors.recurring_interval}</p>}
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-500">Bitiş Tarihi (opsiyonel)</label>
                  <input
                    type="date"
                    disabled={isLoading}
                    value={recurringEndDate}
                    onChange={(e) => {
                      setRecurringEndDate(e.target.value);
                      clearFieldError('recurring_ends_at');
                    }}
                    data-testid="task-recurring-end"
                    className={`w-full rounded-lg border bg-white px-2.5 py-1.5 text-xs outline-none focus:border-blue-500 disabled:bg-slate-100 sm:px-3 sm:py-2 sm:text-sm ${
                      fieldErrors.recurring_ends_at ? 'border-red-300' : 'border-slate-300'
                    }`}
                  />
                  {fieldErrors.recurring_ends_at && <p data-testid="field-error-recurring-ends-at" className="mt-1 text-xs text-red-600">{fieldErrors.recurring_ends_at}</p>}
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-500">Tekrar Sayısı (opsiyonel)</label>
                  <input
                    type="number"
                    min={1}
                    disabled={isLoading}
                    value={recurringOccurrences}
                    onChange={(e) => setRecurringOccurrences(e.target.value ? Number(e.target.value) : '')}
                    data-testid="task-recurring-occurrences"
                    className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-blue-500 disabled:bg-slate-100 sm:px-3 sm:py-2 sm:text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Alt Görevler */}
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <label className="mb-2 block text-xs font-medium text-slate-500">Alt Görevler</label>
            <div className="space-y-2">
              {subTasks.map((st, idx) => (
                <div key={idx} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 sm:px-3 sm:py-2">
                  <input type="checkbox" disabled className="h-4 w-4 rounded border-slate-300 text-blue-600" />
                  <span className="flex-1 text-xs text-slate-700 sm:text-sm">{st.title}</span>
                  <button
                    type="button"
                    onClick={() => removeSubTask(idx)}
                    data-testid={`remove-subtask-${idx}`}
                    className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded text-slate-500 hover:bg-red-50 hover:text-red-500"
                    aria-label="Alt görevi sil"
                    title="Alt görevi sil"
                  >
                    <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </button>
                </div>
              ))}
              <div className="flex gap-2">
                <input
                  value={newSubTask}
                  disabled={isLoading}
                  onChange={(e) => setNewSubTask(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSubTask())}
                  className="min-h-[44px] flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 disabled:bg-slate-100"
                  placeholder="Alt görev ekle..."
                />
                <button
                  type="button"
                  disabled={isLoading}
                  onClick={handleAddSubTask}
                  className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                >
                  <Plus className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Dosya */}
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <label className="mb-2 block text-xs font-medium text-slate-500">Dosyalar</label>
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-center sm:p-6">
              <input
                type="file"
                multiple
                disabled={isLoading}
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
                className="hidden"
                id="task-files"
              />
              <label htmlFor="task-files" className="flex min-h-[56px] cursor-pointer flex-col items-center justify-center gap-2 break-words text-sm text-slate-600">
                <Paperclip className="h-6 w-6 text-slate-500" />
                <span className="text-xs sm:text-sm">Dosya seçmek için tıklayın</span>
                {files.length > 0 && <span className="text-xs text-slate-500">{files.length} dosya seçildi</span>}
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      open
      onClose={onClose}
      align="start"
      overlayClassName="overflow-y-auto bg-black/50 p-2 pt-2 sm:p-3 sm:pt-4 md:p-4 md:pt-10"
      panelUnstyled
      panelClassName="w-full max-w-none rounded-t-xl bg-white p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-xl sm:max-w-2xl sm:rounded-xl sm:p-4 md:p-6 max-h-[85vh] sm:max-h-[90vh] overflow-y-auto"
      labelledBy="task-modal-title"
      data-testid="task-modal"
    >
      <div className="mb-3 flex items-center justify-between sm:mb-4">
          <h2 id="task-modal-title" className="text-base font-bold text-slate-900 sm:text-lg">
            {editTask ? 'Görevi Düzenle' : 'Yeni Görev'}
          </h2>
          <button
            onClick={onClose}
            data-testid="task-modal-close"
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Kapat"
            title="Kapat"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-700 sm:mb-4 sm:px-4 sm:py-2.5 sm:text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          {renderTemplateSection()}

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Başlık *</label>
            <input
              disabled={isLoading}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                clearFieldError('title');
              }}
              data-testid="task-title"
              className={`w-full rounded-lg border bg-white px-2.5 py-1.5 text-xs outline-none focus:border-blue-500 disabled:bg-slate-100 sm:px-3 sm:py-2 sm:text-sm ${
                fieldErrors.title ? 'border-red-300' : 'border-slate-300'
              }`}
              placeholder="Görev başlığı"
            />
            {fieldErrors.title && <p data-testid="field-error-title" className="mt-1 text-xs text-red-600">{fieldErrors.title}</p>}
          </div>

          <div className="relative">
            <label className="mb-1 block text-xs font-medium text-slate-500">Açıklama</label>
            <textarea
              ref={descRef}
              rows={4}
              disabled={isLoading}
              value={description}
              onChange={handleDescChange}
              className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-blue-500 disabled:bg-slate-100 sm:px-3 sm:py-2 sm:text-sm"
              placeholder="Açıklama yazın (@ ile kullanıcı etiketleyin)"
            />
            {mentionOpen && filteredUsers.length > 0 && (
              <div
                className="fixed z-50 max-h-40 w-48 max-w-[calc(100vw-1rem)] overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg sm:w-56"
                style={{ top: mentionPos.top, left: Math.max(8, Math.min(mentionPos.left, window.innerWidth - 200)) }}
              >
                {filteredUsers.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => insertMention(u)}
                    className="flex min-h-[44px] w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50 sm:px-3 sm:py-2 sm:text-sm"
                  >
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-semibold text-blue-700">
                      {u.name[0]}
                    </div>
                    <span className="truncate">{u.name} {u.surname}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Proje *</label>
              <select
                disabled={isLoading}
                value={projectId}
                onChange={(e) => {
                  setProjectId(e.target.value ? Number(e.target.value) : '');
                  clearFieldError('project_id');
                }}
                data-testid="task-project"
                className={`w-full rounded-lg border bg-white px-2.5 py-1.5 text-xs outline-none focus:border-blue-500 disabled:bg-slate-100 sm:px-3 sm:py-2 sm:text-sm ${
                  fieldErrors.project_id ? 'border-red-300' : 'border-slate-300'
                }`}
              >
                <option value="">Proje seç...</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              {fieldErrors.project_id && <p data-testid="field-error-project" className="mt-1 text-xs text-red-600">{fieldErrors.project_id}</p>}
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Öncelik</label>
              <select
                disabled={isLoading}
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                data-testid="task-priority"
                className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-blue-500 disabled:bg-slate-100 sm:px-3 sm:py-2 sm:text-sm"
              >
                <option value="dusuk">Düşük</option>
                <option value="orta">Orta</option>
                <option value="yuksek">Yüksek</option>
                <option value="acil">Acil</option>
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Son Tarih</label>
              <input
                type="date"
                disabled={isLoading}
                value={dueDate}
                onChange={(e) => {
                  setDueDate(e.target.value);
                  clearFieldError('due_date');
                  if (isRecurring) clearFieldError('recurring_ends_at');
                }}
                data-testid="task-due-date"
                className={`w-full rounded-lg border bg-white px-2.5 py-1.5 text-xs outline-none focus:border-blue-500 disabled:bg-slate-100 sm:px-3 sm:py-2 sm:text-sm ${
                  fieldErrors.due_date ? 'border-red-300' : 'border-slate-300'
                }`}
              />
              {fieldErrors.due_date && <p data-testid="field-error-due-date" className="mt-1 text-xs text-red-600">{fieldErrors.due_date}</p>}
            </div>

            <div className="sm:col-span-2">{renderAssigneeSection()}</div>
          </div>

          {renderAdvancedSection()}

          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="min-h-[44px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 sm:px-4"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={isLoading || !projectId}
              data-testid="save-task-button"
              className="flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-60 sm:px-4"
            >
              {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              {editTask ? 'Kaydet' : 'Oluştur'}
            </button>
          </div>
        </form>
    </Modal>
  );
}
